import './SuggestedAmountInput.scss';

import {
  CustomAmountsFormData,
  amountKeys,
} from '../../../types/givingCheckoutForm';
import React, { FunctionComponent } from 'react';

import { ErrorMessage } from '@hookform/error-message';
import classNames from 'classnames';
import { formatCurrencySymbol } from '../../utils/currencyFormatting';
import { useCharityData } from '../../context/charityContext';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = { id: string };

const SuggestedAmountInput: FunctionComponent<Props> = ({ id }) => {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
  } = useFormContext<CustomAmountsFormData>();
  const { charity } = useCharityData();
  const currencySymbol = formatCurrencySymbol(charity?.settlementCurrencyCode);
  const fieldName = `amount${id}` as amountKeys;
  const inputName = `suggestedAmounts.${fieldName}` as const;

  const min = 2;
  const max = 99999;

  const inputClassNames = classNames(
    'jg-form-control suggested-amount__input',
    {
      'jg-form-control--error': Boolean(errors.suggestedAmounts?.[fieldName]),
    },
  );

  return (
    <div className="suggested-amount">
      <label
        className="jg-form-label jg-color--nero jg-space-mbxs"
        htmlFor={inputName}
      >
        {t('suggestedAmounts.amountLabel', { id })}
      </label>
      <div className="suggested-amount__wrapper">
        <div className="suggested-amount__currency">{currencySymbol}</div>
        <input
          type="text"
          className={inputClassNames}
          id={inputName}
          maxLength={5}
          {...register(inputName, {
            required: `${t('suggestedAmountError.required')}`,
            min: {
              value: min,
              message: t('suggestedAmountError.amount', {
                min,
                max,
              }),
            },
            max: {
              value: max,
              message: t('suggestedAmountError.amount', {
                min,
                max,
              }),
            },
            pattern: {
              value: /^([0-9]*)$/,
              message: t('suggestedAmountError.pattern'),
            },
          })}
        />

        {Boolean(errors.suggestedAmounts?.[fieldName]) && (
          <ErrorMessage
            errors={errors}
            name={inputName}
            render={({ message }) => {
              return (
                <p className="jg-form-error-copy" role="alert">
                  {message}
                </p>
              );
            }}
          />
        )}
      </div>
      <input
        type="radio"
        className="jg-radio"
        value={id}
        id={`default-amount${id}`}
        data-qa={`default-amount${id}`}
        {...register('suggestedAmountDefault')}
      />
      <label className="jg-form-label" htmlFor={`default-amount${id}`}>
        {t('suggestedAmounts.makeDefaultLabel')}
      </label>
    </div>
  );
};

export default SuggestedAmountInput;
